import React from 'react';
import { stage as stageEnum } from '../enums';
import { sortByDate } from '../sorting';
import { dateDifference } from '../date';
import { ILine, IStage } from '../../global';
import { INotificationContext, ENotificationTypes } from '../../Types';
import { getStartOfLine, getEndOfLine } from './start-end';
import { getLineDurationInfo } from './timeline';
import moment from 'moment';
import cloneDeep from 'lodash.clonedeep';

export function maxStagesValidation(stages: Array<IStage>) {
  const activeStages = stages.filter((stage) => !stage.deleted);
  const maxStages = activeStages.length >= 7 ? true : false;
  return maxStages;
}

export function minStagesValidation(stages: Array<IStage>) {
  const activeStages = stages.filter((stage) => !stage.deleted);
  const minStages = activeStages.length <= 3 ? true : false;
  return minStages;
}

export function validateLineLength(stages) {
  const start = getStartOfLine(stages);
  const end = getEndOfLine(stages);
  const lineLength = dateDifference(moment(start), moment(end));
  if (lineLength >= 304) {
    throw Error();
  }
}

export function validateLineNameUnique(lines: Array<ILine>, line: ILine) {
  const lineNameNotUnique = lines?.find(
    (l) =>
      l.id !== line.id &&
      line.origin_line !== l.id &&
      line.id !== l.origin_line &&
      l.name === line.name,
  );
  if (lineNameNotUnique) {
    throw Error('LINE_NAME_NOT_UNIQUE');
  }
}

function validateLineDuration(
  line: ILine,
  notification: INotificationContext,
  editLine: (...args) => any,
) {
  const { totalDaysInLine } = getLineDurationInfo(line);
  if (totalDaysInLine > 183) {
    notification.addNotification({
      type: ENotificationTypes.line,
      lineId: line.id,
      message: 'Stock Surplus Risk',
      learnMoreMessage: (
        <span>
          Optimally planned supply chains tend to range between 3-6 months long
          in order to avoid surplus. Moreover, companies with Lines up to 6
          month long spend <b>11% less</b> on logistics, on average.{' '}
          <b>We recommend</b>
          editing your Lines to be between 3-6 months long.
        </span>
      ),
      icon: 'icons/snackbar/important.svg',
      action: {
        name: 'Edit Line',
        onClick: () => {
          editLine(line.id);
        },
      },
    });
  } else if (totalDaysInLine < 60) {
    notification.addNotification({
      type: ENotificationTypes.line,
      lineId: line.id,
      message: 'Protect Your Profit Margin',
      learnMoreMessage: (
        <span>
          Optimally planned supply chains tend to range between 3-6 months long,
          often leading to savings on inventory manufacturing and freight. For
          this reason, Lines between 3-6 months long are <b>41% more likely</b>{' '}
          to be accepted by 8fig’s planning team. <b>We recommend</b> editing
          this Line to be between 3-6 months long.
        </span>
      ),
      icon: 'icons/snackbar/lightBulb.svg',
      action: {
        name: 'Edit Line',
        onClick: () => {
          editLine(line.id);
        },
      },
    });
  }
}

function validateHeavyLine(
  line: ILine,
  notification: INotificationContext,
  editLine: (...args) => any,
) {
  // IF MORE THAN 80% OF FINANCING IS BEFORE STOCK ON HAND DATE
  let amountBeforeStockOnHand: number = 0;
  let amountAfterStockOnHand: number = 0;
  let isAfterStock: boolean = false;
  line.stages.forEach((stage) => {
    if (stage.name === stageEnum.sellingStart.value) isAfterStock = true;
    if (isAfterStock) amountAfterStockOnHand += stage.amount;
    else amountBeforeStockOnHand += stage.amount;
  });
  const totalAmount = amountBeforeStockOnHand + amountAfterStockOnHand;
  if (amountBeforeStockOnHand / totalAmount > 0.8) {
    notification.addNotification({
      type: ENotificationTypes.line,
      lineId: line.id,
      message: 'Potential Cash Flow Imbalance',
      learnMoreMessage: (
        <span>
          Optimally planned supply chains tend to include costs both before and
          after the stock is on hand. Lines with a more balanced cost
          distribution are <b>26% more likely</b> to be accepted by 8fig’s
          planning team. <b>We recommend</b> editing this Line to include costs
          both before and after the stock is on hand.
        </span>
      ),
      icon: 'icons/snackbar/lightBulb.svg',
      action: {
        name: 'Edit Line',
        onClick: () => {
          editLine(line.id);
        },
      },
    });
  }
}

export function validateLine(
  line: ILine,
  notification: INotificationContext,
  editLine: (...args) => any,
) {
  notification.removeNotification('line', line.id);
  const lineCopy = cloneDeep(line);
  lineCopy.stages = lineCopy.stages.filter((line) => !line.deleted);
  lineCopy.stages = sortByDate(line.stages, 'tmpStart');
  validateLineDuration(lineCopy, notification, editLine);
  validateHeavyLine(lineCopy, notification, editLine);
}

export function getOriginLine(line: ILine, lines: Array<ILine>) {
  const originLine = lines?.find(
    (lineFilter) => lineFilter.id === line?.origin_line,
  );
  return originLine;
}
